/**
 * The API's refusal of a schedule or edit call, parsed into an owned shape.
 * Pure and free of the platform registry so it runs under the tsx test
 * runner: it reads the `ApiError` envelope by its fields only.
 */

/** One reason a target was refused, in the contract's vocabulary (`describeRefusalCode` words `code`). */
export type PreflightViolation = {
  code: string;
  /** The form field the violation is about, when the API names one (`privacy`, `title`). */
  field: string | null;
  /** The bound and the media's own value, both in `unit`, for the duration, size and frame codes. */
  limit: number | null;
  actual: number | null;
  unit: 'seconds' | 'bytes' | 'pixels' | null;
};

/** The `error.code` of a schedule or edit call refused at preflight. */
export const PREFLIGHT_FAILED_CODE = 'social.post.preflight_failed';

type ErrorLike = { code?: unknown; data?: unknown };

const asNumber = (value: unknown): number | null => (typeof value === 'number' ? value : null);
const asString = (value: unknown): string | null => (typeof value === 'string' ? value : null);

/**
 * The violations per connection id from a 422 `social.post.preflight_failed`,
 * or null for any other error. A target listed without a usable violation is
 * kept with an empty list: the account was refused, even if this client
 * cannot say why.
 */
export const parsePreflightFailure = (error: unknown): Map<string, PreflightViolation[]> | null => {
  const { code, data } = (error ?? {}) as ErrorLike;
  if (code !== PREFLIGHT_FAILED_CODE) return null;
  const targets = (data as { targets?: unknown } | null)?.targets;
  if (!Array.isArray(targets)) return new Map();
  const byConnection = new Map<string, PreflightViolation[]>();
  for (const target of targets as { connectionId?: unknown; violations?: unknown }[]) {
    const connectionId = asString(target?.connectionId);
    if (!connectionId) continue;
    const violations: PreflightViolation[] = [];
    if (Array.isArray(target.violations)) {
      for (const violation of target.violations as Record<string, unknown>[]) {
        const violationCode = asString(violation?.code);
        if (!violationCode) continue;
        const unit = asString(violation.unit);
        violations.push({
          code: violationCode,
          field: asString(violation.field),
          limit: asNumber(violation.limit),
          actual: asNumber(violation.actual),
          unit: unit === 'seconds' || unit === 'bytes' || unit === 'pixels' ? unit : null,
        });
      }
    }
    byConnection.set(connectionId, violations);
  }
  return byConnection;
};
